import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useSite } from '../content/SiteContext';

// Estilo de fondo consistente con otras secciones
const gradientStyle = {
  background: 'linear-gradient(-45deg, #1e1b4b, #4c1d95, #be185d, #1e293b)',
  backgroundSize: '400% 400%',
  animation: 'gradientBG 15s ease infinite',
  width: '100%',
  height: '100%',
  position: 'absolute',
  inset: 0,
  zIndex: 0,
};

// Agregamos la animación global al documento si no existe
if (typeof document !== 'undefined' && !document.getElementById('gradientBGKeyframes')) {
  const style = document.createElement('style');
  style.id = 'gradientBGKeyframes';
  style.innerHTML = `
    @keyframes gradientBG {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `;
  document.head.appendChild(style);
}


// Componente para contador animado
const AnimatedCounter = ({ end, duration = 2, delay = 0, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const isInView = useInView(countRef, { once: true, amount: 0.5 });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp + delay * 1000;
      
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      
      // Easing function para un efecto más suave
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(easeOut * end);
      
      setCount(currentCount);
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isInView, end, duration, delay]);

  return (
    <span ref={countRef}>
      {prefix}{count}{suffix}
    </span>
  );
};

const AwardsSection = () => {
  const { content, t } = useSite();
  const section = content.awards;
  const awards = section.items || [];
  const awardImages = (section.images || []).map((img) => img.src).filter(Boolean);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredAward, setHoveredAward] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const sectionRef = useRef(null);

  // Obtener años únicos para el filtro
  const years = ['all', ...new Set(awards.map(award => String(award.year)))].sort((a, b) => {
    if (a === 'all') return -1;
    if (b === 'all') return 1;
    return b - a;
  });

  // Filtrar premios por año
  const filteredAwards = selectedYear === 'all'
    ? awards
    : awards.filter(award => String(award.year) === selectedYear);

  const statValue = (value) => {
    if (value === 'auto-awards') return awards.length;
    if (value === 'auto-years') return years.length - 1;
    return parseInt(value, 10) || 0;
  };
  const statColors = ['text-yellow-400', 'text-purple-400', 'text-pink-400', 'text-green-400'];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      {
        threshold: 0.5, // La sección debe estar 50% visible
        rootMargin: '0px'
      }
    );

    const node = sectionRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    
    if (isVisible && awardImages.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % awardImages.length);
      }, 4500); // Cambiar cada 4.5 segundos
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isVisible, awardImages.length]);

  return (
    <section id="premios" className="py-20 md:py-28 relative overflow-hidden" ref={sectionRef}>
      {/* Fondo animado consistente */}
      <div style={gradientStyle}></div>
      {/* Capa de oscurecimiento */}
      <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
      
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 opacity-20 z-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-20">
        {/* Header Section */}
        <div className="text-center mb-16 md:mb-20">
          <motion.h2
            className="section-heading text-white reveal mb-6"
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: 800,
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              lineHeight: '1.1'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {t(section.title1)}{' '}
            <span
              className="text-gradient relative"
              style={{
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                backgroundImage: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 25%, #a78bfa 50%, #f472b6 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradient 3s ease infinite'
              }}
            >
              {t(section.title2)}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-purple-500 to-pink-500 rounded-full"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </span>
          </motion.h2>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          >
            {t(section.subtitle)}
          </motion.p>

          {/* Filtro por años */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {years.map((year) => (
              <motion.button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedYear === year
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg shadow-yellow-500/25'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-gray-700'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {year === 'all' ? t(section.filterAll) : year}
              </motion.button>
            ))}
          </motion.div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 md:gap-12 items-start">
          {/* Galería de imágenes mejorada */}
          <motion.div
            className="xl:col-span-2 relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm rounded-3xl overflow-hidden border border-gray-700/50 shadow-2xl">
              {/* Imagen principal */}
              <div className="relative aspect-[4/5] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={awardImages[currentImageIndex % Math.max(awardImages.length, 1)]}
                    alt="Premios y reconocimientos"
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                  />
                </AnimatePresence>
                
                {/* Overlay con gradiente */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Indicadores de imagen */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {awardImages.map((_, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex
                          ? 'bg-yellow-400 scale-110'
                          : 'bg-white/50 hover:bg-white/70'
                      }`}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  ))}
                </div>
              </div>

              {/* Stats overlay */}
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-xl p-3">
                <div className="text-yellow-400 font-bold text-2xl">{awards.length}+</div>
                <div className="text-white text-sm">{t(section.badgeLabel)}</div>
              </div>
            </div>
          </motion.div>

          {/* Lista de premios mejorada */}
          <motion.div
            className="xl:col-span-3 space-y-4"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedYear}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {filteredAwards.map((award, index) => (
                  <motion.div
                    key={`${index}-${award.year}`}
                    className="group relative"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    onHoverStart={() => setHoveredAward(index)}
                    onHoverEnd={() => setHoveredAward(null)}
                  >
                    {/* Tarjeta de premio */}
                    <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700/50 p-6 shadow-xl group-hover:shadow-2xl group-hover:shadow-yellow-500/10 transition-all duration-500 group-hover:scale-[1.02] group-hover:border-yellow-500/30">
                      
                      {/* Efecto de brillo al hacer hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/0 via-yellow-500/10 to-orange-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      {/* Icono y año */}
                      <div className="flex items-start justify-between mb-4 relative z-10">
                        <motion.div 
                          className="w-12 h-12 flex items-center justify-center"
                          animate={{ rotate: hoveredAward === index ? [0, -10, 10, 0] : 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          <img 
                            src={award.icon} 
                            alt={t(award.title)}
                            className="w-full h-full object-contain filter brightness-0 invert"
                          />
                        </motion.div>
                        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full px-3 py-1">
                          <span className="text-yellow-400 font-bold text-sm">{award.year}</span>
                        </div>
                      </div>

                      {/* Contenido */}
                      <div className="relative z-10">
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-100 transition-colors duration-300">
                          {t(award.title)}
                        </h3>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-400 text-sm">🏢</span>
                            <span className="text-gray-300 font-medium text-sm">{award.client}</span>
                          </div>
                          
                          <div className="inline-block px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full">
                            <span className="text-purple-300 text-xs font-medium">{t(award.category)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Elementos flotantes */}
                      <AnimatePresence>
                        {hoveredAward === index && (
                          <motion.div
                            className="absolute -top-2 -right-2 w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-full blur-xl"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Estadísticas finales */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.01 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
            {(section.stats || []).map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className={`text-3xl font-bold mb-2 ${statColors[index % statColors.length]}`}>
                  <AnimatedCounter end={statValue(stat.value)} duration={1.5} delay={0.02 * (index + 1)} suffix={stat.suffix || ''} />
                </div>
                <div className="text-gray-400 text-sm">{t(stat.label)}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AwardsSection; 